import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface UserSettings {
  full_name: string
  display_name: string
  email: string
  phone: string | null
  photo_url: string | null
  preferences: {
    theme: string
    language: string
    currency: string
    date_format?: string
    time_format?: string
    number_format?: string
    timezone?: string
    start_of_week?: number
    start_of_month?: number
    fiscal_year_start?: number
  }
  notifications: {
    transaction_created: boolean
    transaction_deleted: boolean
    large_transactions: boolean
    recurring_transactions: boolean
    category_changed: boolean
    transfer_complete: boolean
    exchange_rate_updates: boolean
    new_features: boolean
    app_updates: boolean
    security_alerts: boolean
    sync_status: boolean
    budget_approaching: boolean
    budget_exceeded: boolean
    budget_reset: boolean
    show_in_app: boolean
    push_notifications: boolean
    sound_enabled: boolean
    vibration_enabled: boolean
  }
  created_at: string
  updated_at: string
}

const DEFAULT_SETTINGS: UserSettings = {
  full_name: '',
  display_name: '',
  email: '',
  phone: null,
  photo_url: null,
  preferences: {
    theme: 'light',
    language: 'en',
    currency: 'USD',
    date_format: 'MM/DD/YYYY',
    time_format: 'hh:mm a',
    number_format: '#,##0.00',
    timezone: 'UTC',
    start_of_week: 1,
    start_of_month: 1,
    fiscal_year_start: 1
  },
  notifications: {
    transaction_created: true,
    transaction_deleted: true,
    large_transactions: true,
    recurring_transactions: true,
    category_changed: true,
    transfer_complete: true,
    exchange_rate_updates: true,
    new_features: true,
    app_updates: true,
    security_alerts: true,
    sync_status: true,
    budget_approaching: true,
    budget_exceeded: true,
    budget_reset: true,
    show_in_app: true,
    push_notifications: true,
    sound_enabled: true,
    vibration_enabled: true
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
}

export interface ProfileState {
  settings: UserSettings | null;
  isLoading: boolean;
  error: string | null;
  
  setSettings: (settings: UserSettings) => void;
  resetSettings: () => void;
  fetchSettings: (userId: string) => Promise<void>;
  updateSettings: (userId: string, data: Partial<UserSettings>) => Promise<any>;
  updateProfilePhoto: (userId: string, photoUrl: string | null) => Promise<void>;
  refreshUserProfile: (userId: string) => Promise<void>;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set) => ({
      settings: null,
      isLoading: false,
      error: null,

      setSettings: (settings) => set({ settings }),

      resetSettings: () => {
        console.log('Resetting profile settings to defaults')
        set({ 
          settings: null,
          isLoading: false,
          error: null
        })
      },

      fetchSettings: async (userId: string) => {
        // Check if we're already loading settings
        const state = useProfileStore.getState()
        if (state.isLoading) {
          console.log('Profile settings already being fetched, skipping duplicate request')
          return
        }
        
        set({ isLoading: true, error: null })
        try {
          // Get profile data
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (profileError && profileError.code !== 'PGRST116') {
            throw profileError;
          }

          // Also fetch user preferences
          const { data: userPreferences, error: preferencesError } = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          if (preferencesError && preferencesError.code !== 'PGRST116') {
            console.warn('Error fetching user preferences:', preferencesError);
          }
          
          console.log('Profile data loaded:', profileData);
          console.log('User preferences loaded:', userPreferences);

          // Use stored preferences from the profile if available, otherwise use defaults
          const preferences = {
            ...DEFAULT_SETTINGS.preferences,
            ...(profileData?.preferences || {}),
            // Prefer user_preferences table values if available
            theme: userPreferences?.theme || profileData?.preferences?.theme || profileData?.theme || DEFAULT_SETTINGS.preferences.theme,
            language: userPreferences?.language || profileData?.preferences?.language || profileData?.language || DEFAULT_SETTINGS.preferences.language,
            currency: userPreferences?.currency || profileData?.preferences?.currency || profileData?.currency || DEFAULT_SETTINGS.preferences.currency,
            date_format: userPreferences?.date_format || profileData?.preferences?.date_format || profileData?.date_format || DEFAULT_SETTINGS.preferences.date_format,
            time_format: userPreferences?.time_format || profileData?.preferences?.time_format || profileData?.time_format || DEFAULT_SETTINGS.preferences.time_format,
            number_format: userPreferences?.number_format || profileData?.preferences?.number_format || profileData?.number_format || DEFAULT_SETTINGS.preferences.number_format,
            timezone: userPreferences?.timezone || profileData?.preferences?.timezone || profileData?.timezone || DEFAULT_SETTINGS.preferences.timezone,
            start_of_week: profileData?.preferences?.start_of_week || profileData?.first_day_of_week || DEFAULT_SETTINGS.preferences.start_of_week,
            start_of_month: profileData?.preferences?.start_of_month || profileData?.start_of_month || DEFAULT_SETTINGS.preferences.start_of_month,
            fiscal_year_start: profileData?.preferences?.fiscal_year_start || profileData?.fiscal_year_start || DEFAULT_SETTINGS.preferences.fiscal_year_start
          };
          
          console.log('Merged preferences:', preferences);

          // Use stored notifications from the profile if available, otherwise use defaults
          const notifications = {
            ...DEFAULT_SETTINGS.notifications,
            ...(profileData?.notifications || {})
          };

          // Merge profile data with preferences
          const settings: UserSettings = {
            ...DEFAULT_SETTINGS,
            full_name: profileData?.full_name || '',
            display_name: profileData?.display_name || '',
            email: profileData?.email || '',
            phone: profileData?.phone || null,
            photo_url: profileData?.photo_url || profileData?.avatar_url || null,
            preferences,
            notifications,
            created_at: profileData?.created_at || new Date().toISOString(),
            updated_at: profileData?.updated_at || new Date().toISOString()
          };

          console.log('Mapped settings:', settings);
          set({ settings, isLoading: false });
        } catch (error) {
          console.error('Error fetching settings:', error);
          // Don't throw the error, just set it in the state and continue
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch settings',
            isLoading: false,
            // Still set default settings so the app can function
            settings: DEFAULT_SETTINGS
          });
        }
      },

      updateSettings: async (userId: string, data: Partial<UserSettings>) => {
        set({ isLoading: true, error: null })
        
        try {
          // Validate inputs
          if (!userId) {
            throw new Error('User ID is required to update settings')
          }
          
          // Get current timestamp
          const now = new Date().toISOString()
          
          // Prepare updates object for profile table
          const profileUpdates: Record<string, any> = {
            updated_at: now
          }
          
          // Only add these fields if they are in the data object
          if (data.full_name !== undefined) profileUpdates.full_name = data.full_name
          if (data.display_name !== undefined) profileUpdates.display_name = data.display_name
          if (data.email !== undefined) profileUpdates.email = data.email
          if (data.phone !== undefined) profileUpdates.phone = data.phone
          if (data.photo_url !== undefined) profileUpdates.photo_url = data.photo_url
          
          // Update profile basic info (non-preference fields)
          if (Object.keys(profileUpdates).length > 1) { // Check if we have more than just updated_at
            const { error: profileError } = await supabase
              .from('profiles')
              .update(profileUpdates)
              .eq('id', userId)
            
            if (profileError) throw profileError
          }
          
          // Update preferences if needed
          if (data.preferences) {
            try {
              // Use user_preferences table instead of profiles
              const { error: preferencesError } = await supabase
                .from('user_preferences')
                .upsert({
                  id: userId,
                  user_id: userId,
                  theme: data.preferences.theme,
                  language: data.preferences.language,
                  currency: data.preferences.currency,
                  date_format: data.preferences.date_format,
                  time_format: data.preferences.time_format,
                  number_format: data.preferences.number_format,
                  timezone: data.preferences.timezone,
                  preferences: {
                    start_of_week: data.preferences.start_of_week,
                    start_of_month: data.preferences.start_of_month,
                    fiscal_year_start: data.preferences.fiscal_year_start
                  },
                  updated_at: now
                })
              
              if (preferencesError) throw preferencesError;
            } catch (error) {
              console.error('Error updating preferences:', error);
              throw error;
            }
          }
          
          // Update notifications if needed
          if (data.notifications) {
            try {
              // First try to update using the JSONB column
              const { error: notificationsError } = await supabase
                .from('profiles')
                .update({
                  notifications: data.notifications,
                  updated_at: now
                })
                .eq('id', userId);

              if (notificationsError) {
                console.warn('Could not update notifications as JSONB:', notificationsError);
              }
            } catch (error) {
              console.error('Error updating notification settings:', error);
              throw error;
            }
          }

          // After successful update, update local state
          set((state) => {
            if (!state.settings) {
              return {
                settings: null,
                isLoading: false,
                error: null
              };
            }

            // Create a properly merged settings object
            const updatedSettings: UserSettings = {
              ...state.settings,
              full_name: data.full_name !== undefined ? data.full_name : state.settings.full_name,
              display_name: data.display_name !== undefined ? data.display_name : state.settings.display_name,
              email: data.email !== undefined ? data.email : state.settings.email,
              phone: data.phone !== undefined ? data.phone : state.settings.phone,
              photo_url: data.photo_url !== undefined ? data.photo_url : state.settings.photo_url,
              preferences: data.preferences ? {
                ...state.settings.preferences,
                ...data.preferences
              } : state.settings.preferences,
              notifications: data.notifications ? {
                ...state.settings.notifications,
                ...data.notifications
              } : state.settings.notifications,
              updated_at: now
            };
            
            return {
              settings: updatedSettings,
              isLoading: false,
              error: null
            };
          });

          return { success: true };
        } catch (error) {
          console.error('Error updating settings:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update settings',
            isLoading: false
          });
          throw error;
        }
      },

      updateProfilePhoto: async (userId: string, photoUrl: string | null) => {
        set({ isLoading: true, error: null })
        try {
          const { error } = await supabase
            .from('profiles')
            .upsert({
              id: userId,
              photo_url: photoUrl,
              updated_at: new Date().toISOString()
            })

          if (error) throw error

          set((state) => ({
            settings: state.settings ? {
              ...state.settings,
              photo_url: photoUrl,
              updated_at: new Date().toISOString()
            } : null,
            isLoading: false
          }))
        } catch (error) {
          console.error('Error updating profile photo:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to update profile photo',
            isLoading: false
          })
          throw error
        }
      },

      refreshUserProfile: async (userId: string) => {
        try {
          console.log('Refreshing user profile data')
          
          // Get profile data from Supabase
          const { data: userData, error: _ } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (userData) {
            console.log('Profile data loaded:', userData);
            
            // Create default notifications object
            const defaultNotifications = {
              transaction_created: true,
              transaction_deleted: true,
              large_transactions: true,
              recurring_transactions: true,
              category_changed: true,
              transfer_complete: true,
              exchange_rate_updates: true,
              new_features: true,
              app_updates: true,
              security_alerts: true,
              sync_status: true,
              budget_approaching: true,
              budget_exceeded: true,
              budget_reset: true,
              show_in_app: true,
              push_notifications: true,
              sound_enabled: true,
              vibration_enabled: true
            };
            
            // Create preferences object from profile data
            const preferences = {
              theme: userData.preferences?.theme || 'light',
              language: userData.preferences?.language || 'en',
              currency: userData.preferences?.currency || 'USD',
              date_format: userData.preferences?.date_format || 'MM/DD/YYYY',
              time_format: userData.preferences?.time_format || 'hh:mm a',
              number_format: userData.preferences?.number_format || 'thousand_dot'
            };
            
            // Map the data to our UserSettings format
            const settingsData: UserSettings = {
              full_name: userData.full_name || '',
              display_name: userData.display_name || '',
              email: userData.email || '',
              phone: userData.phone || null,
              photo_url: userData.photo_url || userData.avatar_url || null,
              preferences: preferences,
              notifications: {
                ...defaultNotifications,
                ...(userData.notifications || {})
              },
              created_at: userData.created_at || new Date().toISOString(),
              updated_at: userData.updated_at || new Date().toISOString()
            };
            
            console.log('Mapped settings:', settingsData);
            set({ settings: settingsData });
          } else {
            console.log('No profile found during refresh');
          }
        } catch (error) {
          console.error('Error refreshing user profile:', error);
        }
      }
    }),
    {
      name: 'flows-profile',
      storage: createJSONStorage(() => getStorage())
    }
  )
)

